/**
 * PURPOSE: Reunir las acciones principales de preparación competitiva en Fases.
 * RESPONSIBILITIES: Mostrar accesos compactos y sus estados de disponibilidad.
 * COLLABORATORS: FasesArea.jsx y modales de herramientas de fase.
 * ROUTE: winscore/src/area/FasesArea/components/PhaseActionsBar.jsx
 */
import React from "react";
import { FiCalendar, FiGrid, FiPlusCircle, FiUsers } from "react-icons/fi";
import { HiDotsCircleHorizontal } from "react-icons/hi";

const actions = [
  {
    key: "participants",
    label: "Instituciones participantes",
    icon: FiUsers,
  },
  {
    key: "matches",
    label: "Planificar partidos",
    icon: FiPlusCircle,
  },
  {
    key: "positions",
    label: "Crear tabla de posiciones",
    icon: FiGrid,
  },
];

/**
 * Acceso al Centro de Programación (FTR-0028 WG3). Es aditivo y condicional: solo aparece cuando el
 * backend confirma el flag y el usuario es rol 1. Con el flag apagado el módulo responde 404 a todo,
 * así que ofrecer el botón llevaría a una pantalla vacía. Sigue siendo una pista de interfaz — el
 * servidor vuelve a comprobar rol y alcance en cada petición.
 *
 * Va aparte de `actions` a propósito: abre una herramienta completa, no una acción de preparación, y
 * a diferencia de las otras tres NO se apaga con la configuración cerrada (se puede revisar el
 * fixture aunque no se pueda modificar; el Centro lo avisa dentro).
 */
const PhaseActionsBar = ({
  disabled,
  closed,
  loading,
  loadingAction,
  summaryDisabled,
  onParticipants,
  onMatches,
  onPositions,
  onSummary,
  programmingCenterAvailable = false,
  onProgrammingCenter,
}) => {
  const handlers = {
    participants: onParticipants,
    matches: onMatches,
    positions: onPositions,
  };

  return (
    <section className="mb-5 w-full rounded-2xl border border-blue-100 bg-gradient-to-r from-white to-blue-50/70 p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">
            Preparación de competencia
          </p>
          <h2 className="mt-1 text-lg font-black text-slate-900">Acciones de Fases</h2>
        </div>
        <div className="flex items-center gap-2">
          {closed ? (
            <span className="w-fit rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
              Configuración cerrada
            </span>
          ) : loading ? (
            <span className="w-fit rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
              Cargando acciones...
            </span>
          ) : null}
          {programmingCenterAvailable ? (
            <button
              type="button"
              onClick={onProgrammingCenter}
              disabled={loading}
              title="Abrir el Centro de Programación"
              className="flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-blue-300 bg-blue-600 px-4 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <FiCalendar className="text-lg" />
              <span>Centro de Programación</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={onSummary}
            disabled={summaryDisabled}
            title="Resumen de fases"
            aria-label="Abrir resumen de fases"
            // Cambios: quitamos w-9, agregamos px-4, gap-2 y ajustamos el texto
            className="flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-500 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <HiDotsCircleHorizontal className="text-lg" />
            <span>Número de Grupos</span>
          </button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              type="button"
              onClick={handlers[action.key]}
              disabled={disabled || loading}
              className="group flex min-h-[72px] items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-xl text-blue-700 transition group-hover:bg-blue-600 group-hover:text-white">
                <Icon />
              </span>
              <span className="block text-sm font-black text-slate-900">
                {loadingAction === action.key ? "Actualizando..." : action.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default PhaseActionsBar;
